import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { DatePipe, NgForOf, NgIf } from "@angular/common";
import { NgbAlert, NgbCalendar, NgbDate, NgbDateStruct, NgbDropdown, NgbDropdownMenu, NgbDropdownToggle, NgbInputDatepicker, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { NgxPrintDirective, NgxPrintService, PrintOptions } from "ngx-print";
import { NgxSpinnerComponent, NgxSpinnerService } from "ngx-spinner";
import { MessageService } from "@shell/message.service";
import { ModificacionSaldo, ModificacionSaldoClienteDTO } from "@shared/models/controlCredito/ModificacionSaldo";
import { UtilCredito } from "@shared/utiles/UtilCredito";
import { EstadoSolicitudModificada } from "@shared/enums/estadoSolicitudModificada";
import { ModificacionConfiguracionBloqueoDTO } from '@shared/models/controlCredito/ModificacionConfiguracionBloqueoDTO';
import { ConfiguracionComponenteBloqueo } from '@shared/models/controlCredito/Solicitante';
import { ControlCreditoService } from '@controlCredito/control-credito.service';
import { ErrorModel } from '@shared/models/ErrorModel';
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

export interface RespuestaModificaciones {
  modificacionesSaldo: ModificacionSaldo[];
  modificacionesConfiguracionBloqueo: ModificacionConfiguracionBloqueoDTO[];
}

@Component({
  selector: 'app-reporte-autorizaciones',
  standalone: true,
  imports: [
    FormsModule,
    NgForOf,
    NgIf,
    NgbAlert,
    NgbDropdown,
    NgbDropdownMenu,
    NgbDropdownToggle,
    NgbInputDatepicker,
    NgbTooltip,
    NgxPrintDirective,
    ReactiveFormsModule,
    NgxSpinnerComponent,
    DatePipe
  ],
  providers: [UtilCredito],
  templateUrl: './reporte-autorizaciones.component.html',
  styleUrl: './reporte-autorizaciones.component.scss'
})
export class ReporteAutorizacionesComponent implements OnInit, OnDestroy{
  form!: FormGroup;
  modificaciones: ModificacionSaldo[] = [];
  modificacionesBloqueos: ModificacionConfiguracionBloqueoDTO[] = [];
  todasLasModificaciones: RespuestaModificaciones = {
    modificacionesSaldo: [],
    modificacionesConfiguracionBloqueo: []
  };
  obteniendoInformacion = false;
  mostrarModificaciones: { modificacionId: string, mostrar: boolean } [] = [];
  combinedFilteredSorted: Array<{ type: 'saldo' | 'bloqueo', data: any, date: Date }> = [];
  enumEstados: number[] = Object.values(EstadoSolicitudModificada)
    .filter(value => typeof value === 'number') as number[];
  enumEstadoSolicitudCliente = EstadoSolicitudModificada;
  destroy$ = new Subject<void>();

  constructor(  public calendar: NgbCalendar,
                private controlCreditoService: ControlCreditoService,
                private fb: FormBuilder,
                public utilCredito: UtilCredito,
                private spinner: NgxSpinnerService,
                private messageService: MessageService,
                private printService: NgxPrintService) { }

  ngOnInit() {
    this.crearForm()
    const today = this.calendar.getToday();

    this.form.patchValue({
      fechaDesdeFormControl: today,
      fechaHastaFormControl: today
    });

    this.form.get('estadosFormControl')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.filtrarAutorizaciones());

    this.form.get('buscadorFormControl')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.filtrarAutorizaciones());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  crearForm(): void {
    this.form = this.fb.group({
      buscadorFormControl: [''],
      fechaDesdeFormControl: [null, Validators.required],
      fechaHastaFormControl: [null, Validators.required],
      estadosFormControl: this.fb.array(this.enumEstados.map(() => false))
    });

    this.form.setValidators([
      this.validarRangoFecha('fechaDesdeFormControl', 'fechaHastaFormControl')
    ]);
  }

  validarRangoFecha(fechaDesde: string, fechaHasta: string) {
    return (formGroup: FormGroup) => {
      const desdeControl = formGroup.get(fechaDesde);
      const hastaControl = formGroup.get(fechaHasta);

      const desde = desdeControl?.value as NgbDateStruct;
      const hasta = hastaControl?.value as NgbDateStruct;
      const today = this.calendar.getToday();

      // Limpiar errores previos
      desdeControl?.setErrors(null);
      hastaControl?.setErrors(null);

      // Validación 1: fechaDesde no puede ser futura
      if (desde && NgbDate.from(desde).after(NgbDate.from(today))) {
        desdeControl?.setErrors({ fechaFutura: true });
        return { fechaFutura: true };
      }

      // Validación 2: fechaDesde no puede ser posterior a fechaHasta
      if (desde && hasta && NgbDate.from(desde).after(NgbDate.from(hasta))) {
        hastaControl?.setErrors({ rangoInvalido: true });
        return { rangoInvalido: true };
      }

      // Validación 3: diferencia entre fechas no debe superar los 31 días
      if (desde && hasta) {
        const fechaDesdeNgb = NgbDate.from(desde);
        const fechaHastaNgb = NgbDate.from(hasta);

        const fechaDesdeDate = new Date(fechaDesdeNgb.year, fechaDesdeNgb.month - 1, fechaDesdeNgb.day);
        const fechaHastaDate = new Date(fechaHastaNgb.year, fechaHastaNgb.month - 1, fechaHastaNgb.day);

        const diffMs = Math.abs(fechaHastaDate.getTime() - fechaDesdeDate.getTime());
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays > 31) {
          hastaControl?.setErrors({ diferenciaMaximaExcedida: true });
          return { diferenciaMaximaExcedida: true };
        }
      }

      return null;
    };
  }

  formatDate(date: NgbDateStruct | null, esFechaHasta: boolean, imprimir: boolean = false): string {
    if (!date) return '';
    const year = date.year;
    const month = String(date.month).padStart(2, '0'); // Asegura dos dígitos (ej: 05)
    const day = String(date.day).padStart(2, '0');
    let hour = 0;
    let minute = 0;
    if (esFechaHasta) {
      hour = 23;
      minute = 59;
    }
    // Formatear hora y minutos con ceros iniciales
    const hourStr = String(hour).padStart(2, '0');
    const minuteStr = String(minute).padStart(2, '0');

    return imprimir ? `${day}-${month}-${year}T${hourStr}:${minuteStr}` : `${year}-${month}-${day}T${hourStr}:${minuteStr}`;
  }

  obtenerEtiqueta(estado: number): string {
    return EstadoSolicitudModificada[estado];
  }

  obtenerEtiquetaBloqueos(estado: number): string {
    return ConfiguracionComponenteBloqueo[estado];
  }

  obtenerEtiquetas(estadosSeleccionados: boolean[]): string {
    const etiquetas = this.enumEstados
      .filter((_, index) => estadosSeleccionados[index])
      .map(estado => this.obtenerEtiqueta(estado));

    return etiquetas.length > 0
      ? etiquetas.join(', ')
      : 'Aprobado, Rechazado';
  }

  filtrarAutorizaciones() {
    const busqueda = (this.form.get('buscadorFormControl')?.value || '').toLowerCase();
    const estadosSeleccionados = this.enumEstados.filter((_, index) => this.estadosArray.at(index).value);

    // Filtrar modificaciones de saldo
    const modificacionesFiltradasSaldo = this.modificaciones?.filter(m =>
      (estadosSeleccionados.length === 0 || estadosSeleccionados.includes(m.estado)) &&
      (m.clienteRazonSocial.toLowerCase().includes(busqueda) || m.clienteCuit.toLowerCase().includes(busqueda))
    );

    // Filtrar modificaciones de bloqueo
    const modificacionesFiltradasBloqueo = this.modificacionesBloqueos?.filter(m => {
      const estadoExcedido = m.situacionExcedido?.estadoModificacion;
      const estadoVencido = m.situacionVencido?.estadoModificacion;
      const coincideEstado = estadosSeleccionados.length === 0 ||
        estadosSeleccionados.includes(estadoExcedido) ||
        estadosSeleccionados.includes(estadoVencido);
      return coincideEstado &&
        (m.razonSocial.toLowerCase().includes(busqueda) || m.clienteCuit.toLowerCase().includes(busqueda));
    });

    // Reiniciar la lista combinada
    this.combinedFilteredSorted = [];

    // Agregar modificaciones de saldo
    modificacionesFiltradasSaldo?.forEach(m => {
      this.combinedFilteredSorted.push({
        type: 'saldo',
        data: m,
        date: new Date(m.fechaModificacion)
      });
    });

    // Agregar modificaciones de bloqueo con su fecha efectiva
    modificacionesFiltradasBloqueo?.forEach(m => {
      const fechaEfectiva = this.obtenerFechaEfectiva(m);
      if (fechaEfectiva) {
        this.combinedFilteredSorted.push({
          type: 'bloqueo',
          data: m,
          date: fechaEfectiva
        });
      }
    });

    // Ordenar por fecha descendente
    this.combinedFilteredSorted.sort((a, b) => {
      // Manejar fechas inválidas (null/undefined)
      const dateA = a.date?.getTime() || -Infinity;
      const dateB = b.date?.getTime() || -Infinity;
      return dateB - dateA;
    });

    // Reiniciar el array de mostrar/ocultar
    this.iniciarArrayMostrarModificaciones();
  }

  buscarDesdeHasta(){
    this.obteniendoInformacion = true;
    this.spinner.show('spinnerReporteAutorizaciones');
    this.buscador.setValue('');
    this.estadosArray.setValue(this.enumEstados.map(() => false));
    this.controlCreditoService.getReporteAutorizaciones(this.formatDate(this.fechaDesdeFormControl.value, false), this.formatDate(this.fechaHastaFormControl.value, true))
    .subscribe(res => {
      const modificacionesSaldoOrdenadas = res.modificacionesSaldo
        ?.sort((a, b) => new Date(b.fechaModificacion).getTime() - new Date(a.fechaModificacion).getTime()) || [];
      const modificacionesBloqueosOrdenadas = this.ordenarPorFecha(res.modificacionesConfiguracionBloqueo) || [];
      this.todasLasModificaciones.modificacionesSaldo = modificacionesSaldoOrdenadas;
      this.todasLasModificaciones.modificacionesConfiguracionBloqueo =  modificacionesBloqueosOrdenadas;
      this.modificaciones = modificacionesSaldoOrdenadas;
      this.modificacionesBloqueos = modificacionesBloqueosOrdenadas;
      this.filtrarAutorizaciones();
      this.obteniendoInformacion = false;
      this.iniciarArrayMostrarModificaciones();
      this.spinner.hide('spinnerReporteAutorizaciones');
      }, (err: ErrorModel) => {
        this.messageService.showErrorMessage('Hubo un error al obtener los datos. ' + err.description);
        this.spinner.hide('spinnerReporteAutorizaciones');
        this.obteniendoInformacion = false;
      });
  }

  ordenarPorFecha(modificaciones: ModificacionConfiguracionBloqueoDTO[]): ModificacionConfiguracionBloqueoDTO[] {
    if (!modificaciones) return [];
    const modificacionesCopia = [...modificaciones];
    modificacionesCopia.sort((a, b) => {
      // Obtener fechas efectivas
      const fechaA = this.obtenerFechaEfectiva(a);
      const fechaB = this.obtenerFechaEfectiva(b);

      // Manejar casos nulos
      if (!fechaA && !fechaB) return 0;
      if (!fechaA) return 1; // `a` tiene fecha inválida → va después
      if (!fechaB) return -1; // `b` tiene fecha inválida → va después

      // Comparar fechas descendente
      return fechaB.getTime() - fechaA.getTime();
    });
    return modificacionesCopia;
  }

  obtenerFechaEfectiva(modificacion: ModificacionConfiguracionBloqueoDTO): Date | null {
    const { situacionVencido, situacionExcedido } = modificacion;
    if (situacionVencido?.fechaActualizacion) {
      return new Date(situacionVencido.fechaActualizacion);
    }
    if (situacionExcedido?.fechaActualizacion) {
      return new Date(situacionExcedido.fechaActualizacion);
    }
    // Si ninguno tiene fecha válida
    return null;
  }

  iniciarArrayMostrarModificaciones() {
    this.mostrarModificaciones = [];
    this.combinedFilteredSorted.forEach(item => {
      const id = item.data.id; // Extrae el ID del objeto interno (saldo o bloqueo)
      this.mostrarModificaciones.push({ modificacionId: id, mostrar: false });
    });
  }

  mostrarModificacionesCredito(modificacionId: string): boolean {
    const entry = this.mostrarModificaciones.find(
      modif => modif.modificacionId === modificacionId
    );
    return entry ? entry.mostrar : false;
  }

  mostrarOcultarModificacionesCredito(modificacionId: string) {
    const entry = this.mostrarModificaciones.find(
      modif => modif.modificacionId === modificacionId
    );
    if (entry) {
      entry.mostrar = !entry.mostrar;
    }
  }

  obtenerModificacionesCredito(saldoViejoDTO: ModificacionSaldoClienteDTO, saldoNuevoDTO: ModificacionSaldoClienteDTO){
    let modificaciones: { solicitudViejaDTO: string, solicitudNuevaDTO: string } [] = [];

    if(saldoViejoDTO != null && saldoNuevoDTO != null){
      const viejo = saldoViejoDTO;
      const nuevo = saldoNuevoDTO;

      modificaciones = this.utilCredito.compararSolicitudes(viejo, nuevo);
    }
    return modificaciones;
  }

  print() {
    const customPrintOptions: PrintOptions = new PrintOptions({
      printSectionId: 'contenedorPrint',
      useExistingCss: true
    });
    this.printService.print(customPrintOptions)
  }

  //getters
  get estadosArray() {
    return this.form.get('estadosFormControl') as FormArray;
  }

  get buscador(){
    return this.form.get('buscadorFormControl') as FormControl;
  }

  get fechaDesdeFormControl(){
    return this.form.get('fechaDesdeFormControl') as FormArray;
  }

  get fechaHastaFormControl() {
    return this.form.get('fechaHastaFormControl') as FormArray;
  }
}

